/**
 * Баннер для iPhone: iOS не показывает системное окно установки, поэтому
 * подсказываем добавить приложение на «Домой». Закрытый баннер больше не
 * показываем (localStorage), но кнопка в профиле остаётся.
 */

import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { Button } from '../Button';
import { IconX } from '../icons';
import { IosInstallModal } from './IosInstallModal';
import { ShareGlyph } from './ShareGlyph';

const STORAGE_KEY = 'aptile.ios-install-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Приватный режим: просто показываем баннер.
    return false;
  }
}

export function IosInstallBanner() {
  const { t } = useTranslation();
  const state = useInstallPrompt();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  if (!state.isIos || state.installed || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Настройка не сохранится — баннер вернётся в следующий раз.
    }
  };

  return (
    <>
      <div className="mb-4 flex w-full max-w-3xl flex-col gap-3 rounded-card border border-accent/40 bg-accent/10 p-4 sm:flex-row sm:items-center">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent/20 text-accent-dark" aria-hidden="true">
          <ShareGlyph size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-bold text-heading">{t('install.bannerTitle')}</p>
          <p className="text-sm text-muted">{t('install.bannerText')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setInstructionsOpen(true)}>
            {t('install.bannerAction')}
          </Button>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('install.bannerDismiss')}
            className="tap-target grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted transition hover:bg-heading/10 hover:text-heading"
          >
            <IconX size={18} aria-hidden="true" />
          </button>
        </div>
      </div>
      <AnimatePresence>{instructionsOpen && <IosInstallModal onClose={() => setInstructionsOpen(false)} />}</AnimatePresence>
    </>
  );
}
